import { useMemo } from "react"
import PropTypes from "prop-types"
import { Card, IconButton } from "@mui/material"
import { AGGREGATION_SHAPE } from "helpers/factorClassification"
import { computeLitmusCSVData, csvLocation } from "helpers/csv"
import ExcelDownload from "components/excel-download"
import AggregationDropdown, { OPTION_SHAPE } from "components/list-item-dropdown"
import { FACTOR, GROUP, SECURITY, AGGREGATION, BASE } from "./shapes"
import FactorHeaders from "./components/factor-headers"
import DateSelector from "components/date-picker"
import LoadingWrapper from "components/loading-wrapper"
import CategoryHeaders from "./components/category-headers"
import Row from "./components/row"
import "./style.scss"

export default function Litmus(props) {
  const {
    rows,
    groups,
    factors,
    setSort,
    toggleAggregation,
    toggleLitmusView,
    resetSelectedDate,
    openSecurityProfile,
    openFactorProfile,
    addNotification,
    selectedDate,
    setSelectedDate: setSelectededDateProp,
    earliestDate,
    latestDate,
    fileName,
    status,
    unmodeledDates,
    contentSetId,
    displayTypeName,
    displayAggregationToggle,
    setAggregationFn,
    aggregation,
    aggregationOptions,
  } = props

  const toggleHistoricalView = () => {
    resetSelectedDate()
    toggleLitmusView()
  }

  const setSelectedDate = change => {
    if (change.adjusted) {
      addNotification("info", null, `The selected date has been adjusted to the nearest available date: ${change.adjusted}.`)
    }

    setSelectededDateProp(change.adjusted || change.selectedDate)
  }

  const title = `Daily ${displayTypeName} Exposures`

  // computeLitmusCSVData is a pretty expensive function
  const data = useMemo(() => computeLitmusCSVData(factors, rows), [factors, rows])

  return <div className="modules_litmus">
    <Card square elevation={1} className="header">
      <div className="title">{title}</div>
      <DateSelector {...{ earliestDate, latestDate, selectedDate, exclusionMap: unmodeledDates, onChange: setSelectedDate }} />
      {displayAggregationToggle && !contentSetId && <AggregationDropdown setValue={setAggregationFn} value={aggregation} listSubheader="Choose display type for style factors" label="Display Type" options={aggregationOptions} classes={{ toggle: "aggregation-toggle" }} />}
      <div className="buttons">
        <ExcelDownload data={data} fileName={fileName} type="Exposure" location={csvLocation.litmus} />
        <IconButton
          onClick={toggleHistoricalView}
          color="default"
          className="fa fa-compress daily-exposure-switcher-button"
          size="large"
        />
      </div>
    </Card>

    <Card className="daily-exposure-litmus">
      <LoadingWrapper status={[status]}>
        <div className="table-wrapper">
          <div className="table-container">
            <table className="litmus-table">
              <thead>
                <CategoryHeaders groups={groups} />
                <FactorHeaders {...{ factors, setSort }} />
              </thead>
              <tbody>
                {rows.map(row => <Row
                  key={row.key}
                  {...{
                    row,
                    factors,
                    toggleAggregation,
                    openSecurityProfile,
                    openFactorProfile,
                    contentSetId,
                  }}
                />)}
              </tbody>
            </table>
          </div>
        </div>
      </LoadingWrapper>
    </Card>
  </div>
}

Litmus.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.oneOfType([SECURITY, AGGREGATION, BASE])).isRequired,
  groups: PropTypes.arrayOf(GROUP).isRequired,
  factors: PropTypes.arrayOf(FACTOR).isRequired,
  setSort: PropTypes.func.isRequired,
  toggleAggregation: PropTypes.func.isRequired,
  toggleLitmusView: PropTypes.func.isRequired,
  resetSelectedDate: PropTypes.func.isRequired,
  openSecurityProfile: PropTypes.func.isRequired,
  openFactorProfile: PropTypes.func.isRequired,
  addNotification: PropTypes.func.isRequired,
  selectedDate: PropTypes.string.isRequired,
  setSelectedDate: PropTypes.func.isRequired,
  earliestDate: PropTypes.string.isRequired,
  latestDate: PropTypes.string.isRequired,
  fileName: PropTypes.string.isRequired,
  status: PropTypes.string.isRequired,
  unmodeledDates: PropTypes.objectOf(PropTypes.bool).isRequired,
  contentSetId: PropTypes.string,
  displayTypeName: PropTypes.string.isRequired,
  displayAggregationToggle: PropTypes.bool,
  setAggregationFn: PropTypes.func,
  aggregation: AGGREGATION_SHAPE,
  aggregationOptions: PropTypes.arrayOf(OPTION_SHAPE),
}
